import type {
  ChangeLogView,
  ParameterView,
  ParamType,
  ValidRange,
} from '@/api/parameters-client';

// =============================================================================
// param-format.ts — Helpers d'affichage des paramètres (écran A-24).
// =============================================================================
// PUREMENT présentation : libellés humains, indices de plage (UX hint), parsing
// de la saisie en valeur typée pour le PATCH. AUCUNE règle de validation métier
// n'est dupliquée ici : la borne min/max n'est qu'un INDICE affiché ; l'autorité
// de validation reste l'API ([S2-TRANCHÉ #4]). Le parsing convertit seulement la
// chaîne du champ en type attendu (number/boolean) avant l'envoi ; la décision
// d'acceptation/refus appartient au serveur.

/** Libellé humain (FR) d'un param_type — doublé de la couleur du badge (a11y). */
export function paramTypeLabel(type: ParamType): string {
  switch (type) {
    case 'Pourcentage':
      return 'Pourcentage';
    case 'Duree':
      return 'Durée';
    case 'Entier':
      return 'Entier';
    case 'Booleen':
      return 'Booléen';
    case 'Chaine':
      return 'Chaîne';
    default:
      return type;
  }
}

/** Affichage lisible d'une valeur brute (JSON) — pour la liste et l'historique. */
export function formatValue(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }
  if (value === null || value === undefined) {
    return '—';
  }
  return String(value);
}

/**
 * INDICE de plage affiché à l'utilisateur (UX hint, pas une règle). Décrit en
 * clair les bornes connues du valid_range selon le type. N'INTERDIT rien : guide.
 */
export function rangeHint(type: ParamType, range: ValidRange): string {
  if (type === 'Booleen') {
    return 'Valeur attendue : Oui ou Non.';
  }
  if (type === 'Chaine') {
    return 'Texte libre.';
  }
  const parts: string[] = [];
  if (range.min !== undefined) {
    parts.push(`min ${String(range.min)}`);
  }
  if (range.max !== undefined) {
    parts.push(`max ${String(range.max)}`);
  }
  const typeWord = type === 'Entier' ? 'entier' : 'nombre';
  if (parts.length === 0) {
    return `Valeur attendue : un ${typeWord}.`;
  }
  return `Plage autorisée : ${parts.join(', ')}.`;
}

/** Résultat du parsing d'une saisie en valeur typée pour l'envoi PATCH. */
export type ParseResult =
  | { readonly ok: true; readonly value: number | boolean | string }
  | { readonly ok: false; readonly reason: string };

/**
 * Convertit la saisie texte en valeur typée selon param_type, AVANT l'envoi.
 * Ce n'est PAS la validation métier (plage) : seulement une coercition de saisie
 * (« 12 » -> 12, « Oui » -> true). Une saisie non convertible est signalée
 * localement pour éviter d'envoyer un type absurde ; la validation de plage et la
 * décision finale restent au serveur ([S2-TRANCHÉ #4]).
 */
export function parseInputValue(type: ParamType, raw: string): ParseResult {
  if (type === 'Chaine') {
    // TEXTE libre : la saisie EST la valeur. AUCUN trim (un template multi-lignes
    // porte des retours à la ligne et des espaces significatifs — les préserver
    // VERBATIM). La longueur/le contenu ne sont pas jugés ici (autorité serveur).
    return { ok: true, value: raw };
  }
  if (type === 'Booleen') {
    const v = raw.trim().toLowerCase();
    if (v === 'oui' || v === 'true') {
      return { ok: true, value: true };
    }
    if (v === 'non' || v === 'false') {
      return { ok: true, value: false };
    }
    return { ok: false, reason: 'Saisir « Oui » ou « Non ».' };
  }
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: false, reason: 'Saisir une valeur numérique.' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    return { ok: false, reason: 'Saisir un nombre valide.' };
  }
  return { ok: true, value: n };
}

// =============================================================================
// §2.8 — Paramètres SENSIBLES (confirmation textuelle + bandeau d'impact).
// =============================================================================
// Le back N'EXPOSE PAS de marqueur « sensible » (le seed §3.10 ne porte pas de
// champ dédié). Le critère est donc défini CÔTÉ FRONT, par CATÉGORIE — dimension
// stable du référentiel (DBU §2.8 organise les paramètres par catégorie) et
// robuste aux variations de jeu de données (seed réel vs double).
//
// Sont SENSIBLES les catégories qui gouvernent un invariant de premier rang, une
// frontière de sécurité, ou un effet irréversible :
//   - `anonymisation` : seuil K-anonymity — invariant DDD §3 n°4 (CLAUDE.md),
//     pilote le masquage statistique du MarketPositioningSnapshot (DBU §2.8 l.1872).
//   - `tolerances`    : seuil « Hors Jeux » — NOMMÉ sensible dans DBU §2.8 l.1936.
//   - `delais`        : « durées de tokens » (magic link, EditLock, activation) —
//     NOMMÉ sensible dans DBU §2.8 l.1936 (frontière d'authentification/sécurité).
//   - `retention`     : fenêtre de purge/anonymisation RGPD — effet irréversible.
// NON sensibles : `relances` (cadence d'email), `alertes-admin` (alertes dashboard
// non intrusives) — leur modification n'engage ni sécurité ni invariant.
export const SENSITIVE_CATEGORIES: ReadonlySet<string> = new Set<string>([
  'anonymisation',
  'tolerances',
  'delais',
  'retention',
]);

/** Vrai si le paramètre appartient à une catégorie sensible (§2.8, critère front). */
export function isSensitiveParameter(
  param: Pick<ParameterView, 'category'>,
): boolean {
  return SENSITIVE_CATEGORIES.has(param.category);
}

/**
 * Avertissement affiché à l'édition d'un paramètre QUAND des campagnes sont EN COURS
 * (V28-IMPACT-BANNER, §2.8 l.1854 : existence CONDITIONNELLE — le comptage vient du read-model
 * `useCampaignActivity`, PAS d'un statut « sensible »). Le libellé reste générique : app-parameters
 * ne connaît pas le DÉTAIL des campagnes (frontière inter-BC), seulement leur NOMBRE.
 */
export const CAMPAIGN_IMPACT_WARNING =
  'La modification de ce paramètre peut impacter les campagnes en cours.';

// =============================================================================
// §2.8 l.1934 — Cas dérogatoire « propagation immédiate » (V28-IMPACT-BANNER).
// =============================================================================
// « Pour les paramètres marqués comme « propagation immédiate » […], la modification s'applique
// également aux campagnes en cours, avec affichage d'un avertissement renforcé avant validation. »
// La propagation métier RÉELLE (rompre le snapshot de config par campagne) est HORS LOT (plan
// séparé) — ce module ne livre que la CONVENTION du flag + le texte renforcé. Comme pour
// SENSITIVE_CATEGORIES, le back N'EXPOSE PAS de colonne dédiée (C-5, ARCH-reverif) : le critère est
// CÔTÉ FRONT, par CATÉGORIE.
//
// Membre retenu : `alertes-admin` (seul membre — recommandation ARCH, à confirmer SF/GATE_HUMAN,
// DBU l.1934 « à définir en spécification fonctionnelle »). Justification empirique : le seed documente
// ces paramètres comme « Calculées AU VOL côté API » (parameters.seed.ts) — leur modification impacte
// RÉELLEMENT et IMMÉDIATEMENT les campagnes en cours (pas de snapshot figé), exactement le sens de
// l.1934. `anonymisation` (k_anonymity) est EXCLU : DBU §2.8 l.1872 « prend effet aux calculs
// POSTÉRIEURS » (les analyses déjà produites figent leur K) = effet différé, PAS immédiat.
export const IMMEDIATE_PROPAGATION_CATEGORIES: ReadonlySet<string> = new Set<string>([
  'alertes-admin',
]);

/** Vrai si le paramètre appartient à une catégorie « propagation immédiate » (§2.8 l.1934). */
export function isImmediatePropagationParameter(
  param: Pick<ParameterView, 'category'>,
): boolean {
  return IMMEDIATE_PROPAGATION_CATEGORIES.has(param.category);
}

/**
 * Avertissement RENFORCÉ (§2.8 l.1934) — DISTINCT du bandeau générique (CAMPAIGN_IMPACT_WARNING) :
 * affiché EN PLUS quand le paramètre édité est à « propagation immédiate » ET que des campagnes sont
 * en cours. Le ton signale explicitement l'absence de snapshot (application immédiate), ce que le
 * bandeau générique ne dit pas.
 */
export const REINFORCED_PROPAGATION_WARNING =
  "Ce paramètre est à propagation IMMÉDIATE : la modification s'applique sans délai aux campagnes " +
  'en cours (aucun effet différé). Vérifiez son impact avant de valider.';

// =============================================================================
// Export CSV du référentiel de paramètres (§2.8 l.1950), généré CÔTÉ FRONT.
// =============================================================================
// Sécurité DURE (OBS-1) : anti-injection-formule. Une cellule dont le premier
// caractère est interprétable comme une formule par un tableur (=, +, -, @) ou
// comme une séquence de contrôle (tabulation, retour chariot) est neutralisée en
// la préfixant d'une apostrophe. Puis échappement CSV standard (RFC 4180).

/** Caractères de tête déclenchant une exécution de formule (Excel/Sheets/LibreOffice). */
const CSV_FORMULA_PREFIXES: readonly string[] = ['=', '+', '-', '@', '\t', '\r'];

/** En-têtes de colonnes de l'export (ordre stable). */
const CSV_HEADERS: readonly string[] = [
  'Clé',
  'Type',
  'Valeur courante',
  'Valeur par défaut',
  'Plage min',
  'Plage max',
  'Catégorie',
  'Description',
  'Modifié',
  'Sensible',
  'Dernière modification par',
  'Dernière modification le',
];

/**
 * Neutralise et échappe UNE cellule CSV. D'abord l'anti-injection-formule (OBS-1)
 * — préfixe apostrophe si tête dangereuse —, puis l'échappement RFC 4180 (guillemets
 * doublés + encadrement si séparateur / guillemet / saut de ligne présents).
 */
export function csvEscapeCell(raw: string): string {
  let cell = raw;
  // OBS-1 — anti-injection-formule : la cellule ne doit JAMAIS être évaluée.
  if (cell.length > 0 && CSV_FORMULA_PREFIXES.includes(cell.charAt(0))) {
    cell = `'${cell}`;
  }
  // RFC 4180 — encadrement + doublement des guillemets si nécessaire.
  if (/["\r\n,]/.test(cell)) {
    cell = `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

/**
 * Sérialise le référentiel de paramètres en CSV (RFC 4180, lignes CRLF). Chaque
 * cellule passe par `csvEscapeCell` (anti-injection-formule OBS-1 + échappement).
 * Fonction PURE : la donnée est déjà chargée côté front (aucun appel réseau).
 */
export function buildParametersCsv(params: readonly ParameterView[]): string {
  const lines: string[] = [CSV_HEADERS.map(csvEscapeCell).join(',')];
  for (const p of params) {
    const cells: string[] = [
      p.paramKey,
      paramTypeLabel(p.paramType),
      formatValue(p.value),
      formatValue(p.defaultValue),
      p.validRange.min !== undefined ? String(p.validRange.min) : '',
      p.validRange.max !== undefined ? String(p.validRange.max) : '',
      p.category,
      p.description,
      p.value !== p.defaultValue ? 'Oui' : 'Non',
      isSensitiveParameter(p) ? 'Oui' : 'Non',
      p.lastModifiedBy ?? '',
      p.lastModifiedAt ?? '',
    ];
    lines.push(cells.map(csvEscapeCell).join(','));
  }
  return lines.join('\r\n');
}

/** Nom de fichier explicite et horodaté pour l'export CSV (audit externe, §2.8). */
export function parametersCsvFileName(now: Date = new Date()): string {
  const iso = now.toISOString().slice(0, 10);
  return `parametres-applicatifs-${iso}.csv`;
}

// =============================================================================
// §2.8 l.1848-1850 — Colonne UNITÉ du tableau (V28-UI-COMPLETUDE, item 6).
// =============================================================================
// C-5 (ARCH-reverif) : AUCUN champ DB dédié, AUCUNE migration — l'unité est
// DÉRIVÉE côté affichage. `param_type` SEUL est insuffisant : `Duree` recouvre
// trois granularités réelles dans le seed (`*_ttl_minutes`, `*_ttl_hours`/`*_hours`,
// `data_retention_months`) — une dérivation param_type-seul afficherait « durée »
// identique pour des paramètres dont l'unité réelle diffère, ce qui vide la
// colonne de sa valeur d'audit (DBU l.1848 « présente … unité »). Option A
// (recommandation ARCH, retenue GATE_HUMAN) : compléter `param_type` du SUFFIXE
// de `paramKey`, sans ajouter aucun champ ni migration — la clé est déjà une
// donnée d'affichage disponible.

/** Suffixe de `paramKey` -> unité de durée (ordre non signifiant, un seul matche). */
const DURATION_KEY_SUFFIX_UNITS: ReadonlyArray<readonly [string, string]> = [
  ['_minutes', 'minutes'],
  ['_hours', 'heures'],
  ['_months', 'mois'],
  ['_days', 'jours'],
];

/**
 * Unité affichée pour un paramètre (colonne « Unité », DBU §2.8 l.1848). Dérivée de
 * `param_type` + suffixe de `paramKey` pour `Duree` (Option A) ; repli `'durée'` si
 * aucun suffixe connu. Aucune donnée serveur nouvelle : `paramKey`/`paramType` sont
 * déjà exposés par `ParameterView`.
 */
export function paramUnit(
  param: Pick<ParameterView, 'paramType' | 'paramKey'>,
): string {
  switch (param.paramType) {
    case 'Pourcentage':
      return '%';
    case 'Duree': {
      const match = DURATION_KEY_SUFFIX_UNITS.find(([suffix]) =>
        param.paramKey.endsWith(suffix),
      );
      return match !== undefined ? match[1] : 'durée';
    }
    case 'Entier':
    case 'Booleen':
    case 'Chaine':
    default:
      return '';
  }
}

// =============================================================================
// §2.8 l.1950 — Export CSV de l'historique d'UN paramètre (V28-UI-COMPLETUDE, item 5).
// =============================================================================
// « Export du référentiel de paramètres ET DE SON HISTORIQUE » : distinct de
// `buildParametersCsv` (le référentiel). Source = `entries: ChangeLogView[]` déjà
// chargés par `useParameterChangelog(paramKey)` (aucun endpoint back dédié, l'export
// porte sur l'historique du paramètre SÉLECTIONNÉ). Réutilise IMPÉRATIVEMENT
// `csvEscapeCell` (anti-injection-formule OBS-1) — aucun sérialiseur nu.

/** En-têtes de colonnes de l'export de l'historique d'un paramètre (ordre stable). */
const CHANGELOG_CSV_HEADERS: readonly string[] = [
  'Clé',
  'Date',
  'Ancienne valeur',
  'Nouvelle valeur',
  'Auteur',
  'Motif',
];

/**
 * Sérialise l'historique d'UN paramètre en CSV (RFC 4180, lignes CRLF). Chaque
 * cellule passe par `csvEscapeCell` (anti-injection-formule OBS-1 + échappement),
 * exactement comme `buildParametersCsv`. Fonction PURE : aucun appel réseau.
 */
export function buildChangelogCsv(
  paramKey: string,
  entries: readonly ChangeLogView[],
): string {
  const lines: string[] = [
    CHANGELOG_CSV_HEADERS.map(csvEscapeCell).join(','),
  ];
  for (const entry of entries) {
    const cells: string[] = [
      paramKey,
      entry.changedAt,
      formatValue(entry.oldValue),
      formatValue(entry.newValue),
      entry.changedBy,
      entry.reason ?? '',
    ];
    lines.push(cells.map(csvEscapeCell).join(','));
  }
  return lines.join('\r\n');
}

/** Nom de fichier explicite et horodaté pour l'export de l'historique (§2.8 l.1950). */
export function changelogCsvFileName(
  paramKey: string,
  now: Date = new Date(),
): string {
  const iso = now.toISOString().slice(0, 10);
  return `historique-${paramKey}-${iso}.csv`;
}
